// Why a meeting's run is retrying or failed, as the owner's page says it.
//
// The run keeps one closed code per failed attempt (`meeting_run.
// last_error_code`, `apps/server/src/meetings/run.ts` `errorCodeOf`): a step
// error's own code (`model_error:timeout`, `stage_missing_tag`, …),
// `recording_pending` / `transcript_pending` (Google is not done yet),
// `snapshot_<kind>`, `google_<kind>`, `oauth_<kind>`, `openai_<kind>`,
// `audio_<kind>`, `lease_expired` or `internal`. The page needs fewer words than that:
// `runErrorKindOf` folds a code into one of `RUN_ERROR_KINDS`. Content-free
// both ways: codes and kinds are closed tokens, never a message.

/** The prefix of a failed model call's step code: `model_error:<kind>`. */
export const MODEL_ERROR_CODE = "model_error";

/** What the owner's page says about a step's last failure. */
export const RUN_ERROR_KINDS = [
  "model_timeout",
  "model_rate_limit",
  "model_unavailable",
  "model_truncated",
  "model_answer",
  "model_key_rejected",
  "model_other",
  "google_pending",
  "google",
  "project",
  "transcription",
  "audio",
  "interrupted",
  "internal",
] as const;
export type RunErrorKind = (typeof RUN_ERROR_KINDS)[number];

/** A failed model call's kind (`modelFailureOf` in `@loom/workflows`) as the page's kind. */
const MODEL_KINDS: Readonly<Record<string, RunErrorKind>> = {
  timeout: "model_timeout",
  rate_limit: "model_rate_limit",
  overloaded: "model_unavailable",
  server_error: "model_unavailable",
  network: "model_unavailable",
  // Cut at its output cap: too long, not wrong. Told apart from an answer
  // that came back whole but not in the form asked for (`schema`).
  truncated: "model_truncated",
  schema: "model_answer",
  key_rejected: "model_key_rejected",
};

/** The page's kind for a stored run error code; null for none. */
export const runErrorKindOf = (code: string | null): RunErrorKind | null => {
  if (code === null || code === "") return null;
  if (code === MODEL_ERROR_CODE) return "model_other";
  if (code.startsWith(`${MODEL_ERROR_CODE}:`)) {
    return MODEL_KINDS[code.slice(MODEL_ERROR_CODE.length + 1)] ?? "model_other";
  }
  // Google has not finished the recording or its transcript yet: a wait,
  // not a fault.
  if (code === "recording_pending" || code === "transcript_pending") return "google_pending";
  if (code === "no_recording_file") return "google";
  if (code.startsWith("google_") || code.startsWith("oauth_")) return "google";
  // The server could not read the project's issues (`snapshot_<kind>`).
  if (code.startsWith("snapshot_") && code !== "snapshot_key_mismatch") return "project";
  if (code.startsWith("openai_")) return "transcription";
  if (code.startsWith("audio_")) return "audio";
  if (code === "lease_expired") return "interrupted";
  return "internal";
};
